import { ReactElement } from 'react';
import { CheckIcon, MailIcon, WarningIcon } from '../../../../assets/icons';
import { Button, RequestTypeBadge } from '../../../../core/components';
import { useIsDesktop, usePageTitle } from '../../../../core/hooks';
import { tokens } from '../../../../core/theme';
import { REQUEST_TYPE_CONFIG, RequestType } from '../../../../core/types';
import { useAppNavigate } from '../../../../routes';
import { FORM_MAX_WIDTH } from '../../constants';
import { useAutoFocusRef, useInactivityRedirect } from '../../hooks';
import { Channel, CHANNEL_CONFIG } from '../../types/channel';

const INACTIVITY_TIMEOUT_MS = 60_000;
const ALERT_ICON_SIZE = 20;
const ALERT_ICON_GAP = 8;
const CONTENT_GAP = 16;
const CARDS_GAP = 8;
const CARD_ICON_ROW_GAP = 8;
const CARD_PADDING = '12px 16px';
const MOBILE_BADGE_TO_MESSAGE_GAP = 16;
const DESKTOP_BADGE_TO_MESSAGE_GAP = 24;
const MOBILE_HEADER_TO_CARDS_GAP = 24;
const DESKTOP_HEADER_TO_CARDS_GAP = 30;
const MOBILE_CHECK_TO_TITLE_GAP = 4;
const DESKTOP_CHECK_TO_TITLE_GAP = 16;
const MOBILE_ICON_SIZE = 24;
const DESKTOP_ICON_SIZE = 32;
const MOBILE_CHECK_CIRCLE_SIZE = 32;
const DESKTOP_CHECK_CIRCLE_SIZE = 44;

type SuccessStepProps = {
  type: RequestType;
  channel: Channel;
  themeLabel: string | undefined;
  description: string;
  ticketId: string;
  confirmationEmail?: string;
  onNewRequest: () => void;
};

export const SuccessStep = ({
  type,
  channel,
  themeLabel,
  description,
  ticketId,
  confirmationEmail,
  onNewRequest,
}: SuccessStepProps): ReactElement => {
  const isDesktop = useIsDesktop();
  const { icon: ChannelIcon, title: channelTitle } = CHANNEL_CONFIG[channel];
  const { icon: requestTypeIcon, label: requestTypeLabel } = REQUEST_TYPE_CONFIG[type];
  const iconSize = isDesktop ? DESKTOP_ICON_SIZE : MOBILE_ICON_SIZE;
  const checkCircleSize = isDesktop ? DESKTOP_CHECK_CIRCLE_SIZE : MOBILE_CHECK_CIRCLE_SIZE;
  const headingRef = useAutoFocusRef<HTMLHeadingElement>();
  const appNavigate = useAppNavigate();
  const { isWarningVisible, remainingSeconds, cancelRedirect } = useInactivityRedirect({
    timeoutMs: INACTIVITY_TIMEOUT_MS,
    onTimeout: () => appNavigate('home', { replace: true }),
  });
  usePageTitle(`${requestTypeLabel} — Solicitação enviada com sucesso`);

  return (
    <div style={styles.page}>
      <div style={{ ...styles.form, gap: CONTENT_GAP }}>
        <div
          style={{
            ...styles.headerAndMessage,
            gap: isDesktop ? DESKTOP_BADGE_TO_MESSAGE_GAP : MOBILE_BADGE_TO_MESSAGE_GAP,
          }}
        >
          <RequestTypeBadge icon={requestTypeIcon} label={requestTypeLabel} />
          <div
            style={{
              ...styles.messageBlock,
              gap: isDesktop ? DESKTOP_HEADER_TO_CARDS_GAP : MOBILE_HEADER_TO_CARDS_GAP,
            }}
          >
            <div
              style={{
                ...styles.messageHeader,
                gap: isDesktop ? DESKTOP_CHECK_TO_TITLE_GAP : MOBILE_CHECK_TO_TITLE_GAP,
              }}
            >
              <div
                style={{
                  ...styles.checkCircle,
                  width: checkCircleSize,
                  height: checkCircleSize,
                }}
              >
                <CheckIcon
                  width={isDesktop ? 23 : 17}
                  height={isDesktop ? 17 : 12}
                  aria-hidden="true"
                  focusable="false"
                />
              </div>
              <h1 ref={headingRef} tabIndex={-1} style={styles.title}>
                Solicitação enviada com sucesso
              </h1>
            </div>
            <div style={{ ...styles.cardsContainer, gap: CARDS_GAP }}>
              <div style={{ ...styles.protocolCard, padding: CARD_PADDING }}>
                <p style={styles.protocolLabel}>Número do Protocolo</p>
                <div style={styles.divider} />
                <p style={styles.protocolValue}>{ticketId}</p>
              </div>
              {confirmationEmail && (
                <div style={{ ...styles.infoCard, padding: CARD_PADDING }}>
                  <div style={{ ...styles.iconRow, gap: CARD_ICON_ROW_GAP }}>
                    <MailIcon
                      width={iconSize}
                      height={iconSize}
                      aria-hidden="true"
                      focusable="false"
                    />
                    <p style={styles.cardTitle}>Confirmação de E-mail</p>
                  </div>
                  <div style={styles.divider} />
                  <p style={styles.description}>
                    Enviada para <strong>{confirmationEmail}</strong>
                  </p>
                </div>
              )}
              <div style={{ ...styles.infoCard, padding: CARD_PADDING }}>
                <div style={{ ...styles.iconRow, gap: CARD_ICON_ROW_GAP }}>
                  <ChannelIcon
                    width={iconSize}
                    height={iconSize}
                    aria-hidden="true"
                    focusable="false"
                  />
                  <p style={styles.cardTitle}>{channelTitle}</p>
                </div>
                {themeLabel && <p style={styles.themeLabel}>{themeLabel}</p>}
                <div style={styles.divider} />
                <p style={styles.description}>{description}</p>
              </div>
            </div>
            {isWarningVisible && (
              <div style={{ ...styles.inactivityAlert, gap: ALERT_ICON_GAP }}>
                <div style={{ ...styles.inactivityAlertRow, gap: ALERT_ICON_GAP }}>
                  <WarningIcon
                    width={ALERT_ICON_SIZE}
                    height={ALERT_ICON_SIZE}
                    color={tokens.colors.brand.orange.light150}
                    aria-hidden="true"
                    focusable="false"
                  />
                  <div style={styles.inactivityAlertTextGroup}>
                    <p role="alert" style={styles.inactivityAlertText}>
                      Você será redirecionado(a) para o início por inatividade.
                    </p>
                    <p style={styles.inactivityAlertCountdown}>
                      {remainingSeconds} segundos restantes
                    </p>
                  </div>
                </div>
                <div style={styles.inactivityAlertActions}>
                  <Button variant="secondary" onClick={cancelRedirect} expanded={!isDesktop}>
                    Continuar nesta página
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            width: '100%',
            marginTop: isDesktop ? 0 : 'auto',
            marginBottom: isDesktop ? 0 : tokens.spacing.small,
          }}
        >
          <Button variant="primary" onClick={onNewRequest} expanded={!isDesktop}>
            Nova Solicitação
          </Button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    minHeight: '100%',
    padding: `${tokens.spacing.default} ${tokens.spacing.pageGutter}`,
    boxSizing: 'border-box',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: FORM_MAX_WIDTH,
    flex: 1,
    boxSizing: 'border-box',
  },
  headerAndMessage: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  messageBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  messageHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  checkCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: tokens.colors.brand.green.DEFAULT,
  },
  title: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.subtitle,
    fontWeight: tokens.typography.fontWeight.medium,
    color: tokens.colors.global.black,
    textAlign: 'center',
  },
  cardsContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  protocolCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacing.small,
    width: '100%',
    boxSizing: 'border-box',
    border: `1px solid ${tokens.colors.brand.green.DEFAULT}`,
    borderRadius: tokens.radius.small,
    backgroundColor: tokens.colors.global.white,
  },
  protocolLabel: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.input,
    fontWeight: tokens.typography.fontWeight.medium,
    color: tokens.colors.brand.gray.DEFAULT,
  },
  protocolValue: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.input,
    fontWeight: tokens.typography.fontWeight.medium,
    color: tokens.colors.global.black,
  },
  infoCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing.small,
    width: '100%',
    boxSizing: 'border-box',
    border: `1px solid ${tokens.colors.brand.gray.light850}`,
    borderRadius: tokens.radius.small,
    backgroundColor: tokens.colors.global.white,
  },
  iconRow: {
    display: 'flex',
    alignItems: 'center',
  },
  cardTitle: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.input,
    fontWeight: tokens.typography.fontWeight.medium,
    color: tokens.colors.global.black,
  },
  themeLabel: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.caption,
    fontWeight: tokens.typography.fontWeight.medium,
    color: tokens.colors.brand.gray.DEFAULT,
  },
  divider: {
    height: 1,
    width: '100%',
    backgroundColor: tokens.colors.brand.gray.light850,
  },
  description: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.caption,
    fontWeight: tokens.typography.fontWeight.regular,
    color: tokens.colors.brand.gray.DEFAULT,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  inactivityAlert: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    boxSizing: 'border-box',
    marginTop: tokens.spacing.small,
    padding: CARD_PADDING,
    border: `1px solid ${tokens.colors.brand.orange.light150}`,
    borderRadius: tokens.radius.small,
    backgroundColor: tokens.colors.brand.orange.light950,
  },
  inactivityAlertRow: {
    display: 'flex',
    alignItems: 'center',
  },
  inactivityAlertTextGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing.tiny,
  },
  inactivityAlertText: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.disclaimer,
    color: tokens.colors.brand.orange.dark150,
  },
  inactivityAlertCountdown: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.disclaimer,
    fontWeight: tokens.typography.fontWeight.medium,
    color: tokens.colors.brand.orange.dark150,
  },
  inactivityAlertActions: {
    width: '100%',
    marginTop: tokens.spacing.small,
  },
};
